import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { DEFAULT_HIDDEN_FIELDS, FIELD_DEFS } from '../reservationFormConfig';
import { stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn } from '../adminlayout/designStandard';

const REGISTRATION_FIELD_DEFS = FIELD_DEFS.filter((field) => !String(field.key || '').startsWith('quote.'));

export default function FieldSettingsModal({ onClose }) {
  const overlayCloseProps = useOverlayClickClose(() => onClose(false));
  const [hidden, setHidden] = useState(DEFAULT_HIDDEN_FIELDS);
  const [saving, setSaving] = useState(false);
  useBodyScrollLock(true);
  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);
  const groups = useMemo(() => {
    return REGISTRATION_FIELD_DEFS.reduce((acc, field) => {
      acc[field.group] = acc[field.group] || [];
      acc[field.group].push(field);
      return acc;
    }, {});
  }, []);

  useEffect(() => {
    apiClient.get('/api/admin/reservations/field-settings') // 회사는 서버가 보는 사람으로 정한다(2026-10-27)
      .then((res) => {
        if (res.ok && res.data?.success && Array.isArray(res.data.data?.hidden_fields)) {
          setHidden(res.data.data.hidden_fields);
        }
      })
      .catch(() => {});
  }, []);

  const toggleVisible = (key) => {
    if (REGISTRATION_FIELD_DEFS.find((field) => field.key === key)?.alwaysVisible) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return [...next];
    });
  };

  async function handleSave() {
    setSaving(true);
    try {
      const res = await apiClient.put('/api/admin/reservations/field-settings', {
        hidden_fields: hidden,
      });
      if (res.ok && res.data?.success) onClose(true);
      else alert(res.data?.message || '저장 실패');
    } catch (e) {
      alert('저장 오류: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={overlay} {...overlayCloseProps}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={stdModalHeaderBar(8)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>⚙️</div>
            <div style={stdModalHeaderTitle}>입력항목 설정</div>
          </div>
          <button onClick={() => onClose(false)} style={stdModalCloseBtn}>✕</button>
        </div>
        <div style={body}>
          {Object.entries(groups).map(([group, fields]) => (
            <section key={group} style={sectionStyle}>
              <div style={groupTitle}>{group}</div>
              <div style={grid}>
                {fields.map((field) => (
                  <label key={field.key} style={checkRow}>
                    <input
                      type="checkbox"
                      checked={field.alwaysVisible || !hiddenSet.has(field.key)}
                      disabled={field.alwaysVisible}
                      onChange={() => toggleVisible(field.key)}
                    />
                    <span>
                      {field.label}
                      {field.alwaysVisible ? ' (필수)' : ''}
                      {field.readOnly ? ' (자동계산)' : ''}
                    </span>
                  </label>
                ))}
              </div>
            </section>
          ))}
        </div>
        <div style={footer}>
          <button onClick={() => setHidden(DEFAULT_HIDDEN_FIELDS)} style={secondaryBtn}>기본값</button>
          <button onClick={() => onClose(false)} style={secondaryBtn}>취소</button>
          <button onClick={handleSave} disabled={saving} style={saveBtn}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' };
const modal = { width: 'min(760px, calc(100vw - 24px))', maxHeight: 'min(90vh, calc(100dvh - 24px))', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' };
const body = { padding: 16, overflowY: 'auto', display: 'grid', gap: 14 };
const footer = { padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 };
const sectionStyle = { display: 'grid', gap: 8 };
const groupTitle = { fontSize: 12, fontWeight: 800, color: 'var(--text-muted)' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 };
const checkRow = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text)' };
const secondaryBtn = { padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' };
const saveBtn = { padding: '6px 14px', border: 'none', borderRadius: 4, background: 'var(--accent-700, #7c3aed)', color: '#fff', cursor: 'pointer', fontWeight: 700 };
